import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * The centred column a source shows when its pane holds no list to read.
 *
 * One component rather than two that agree today: this dialog's two empty states had already
 * drifted to different heading sizes and measures, and a shared frame is what stops the next one
 * diverging again. The graphic sits in a box of fixed size, so the title lands at the same height
 * whichever source owns the pane and however much art the caller has to hand.
 */

/** The reserved graphic box. Fixed so the title does not move between sources or between stages. */
const GRAPHIC_SIZE = 140;

type ImportEmptyStateProps = {
    /** Centred above the title. A spinner, a plate, an illustration: whatever the stage has. */
    graphic?: ReactNode;
    title: string;
    /** The title answers something the user just did, rather than standing as an invitation. */
    isAlert?: boolean;
    /** Between the title and the description: what this source offers pressing. */
    actions?: ReactNode;
    description?: string;
    /** Last. A refusal, or whatever the caller has to say after the fact. */
    footer?: ReactNode;
};

export default function ImportEmptyState({
    graphic,
    title,
    isAlert = false,
    actions,
    description,
    footer,
}: ImportEmptyStateProps) {
    return (
        <Box
            sx={{
                flex: 1,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: '12px',
                p: 4,
            }}
        >
            {graphic !== undefined && (
                <Box
                    sx={{
                        flex: 'none',
                        width: GRAPHIC_SIZE,
                        height: GRAPHIC_SIZE,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {graphic}
                </Box>
            )}
            <Typography
                role={isAlert ? 'alert' : undefined}
                sx={{ fontSize: 15, fontWeight: 500 }}
            >
                {title}
            </Typography>
            {actions}
            {description !== undefined && (
                <Typography
                    sx={{
                        fontSize: 13,
                        color: 'text.secondary',
                        maxWidth: 420,
                        lineHeight: 1.5,
                    }}
                >
                    {description}
                </Typography>
            )}
            {footer}
        </Box>
    );
}
